import {Button, Table, Tag} from 'antd';
import {useState} from 'react';

import {SelectedCompaniesModal} from '../../../../component/posts/selected-companies/selected-companies-modal';
import {EditPromotionFormModal} from '../../../../component/promotions/promotion-form/edit-promotion-form-modal';
import {PromotionPreview} from '../../../../component/promotions/promotion-preview/promotion-preview';
import {
    getPromotionCatalogStatusConfig,
    getPromotionStatusConfig,
} from '../../../../component/promotions/promotions-helper';
import {Locale} from '../../../../provider/locale/locale';
import {useLocale} from '../../../../provider/locale/locale-hook';
import {usePromotions, usePromotionSelectedCompanies} from '../../../../service/promotions/promotions-hook';
import {PromotionListItemType, PromotionSelectedCompanyType} from '../../../../service/promotions/promotions-type';
import {PublicationListFilterType} from '../../../../service/publication/publication-filter-type';
import {useFormat} from '../../../../util/format-hook/format-hook';

import {PromotionActions} from './promotion-actions';
import {promotionsTableColumnList} from './promotions-table-const';
import {PromotionsTableRenderingType} from './promotions-table-type';

type PropsType = {
    filter?: PublicationListFilterType;
};

export function PromotionsTable({filter}: PropsType): JSX.Element {
    const {data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage} = usePromotions(filter, {
        staleTime: Number.POSITIVE_INFINITY,
    });
    const {getFormattedDateTime} = useFormat();
    const {getLocalizedString} = useLocale();

    const [activePromotionId, setActivePromotionId] = useState<number | null>(null);
    const [editingPromotionId, setEditingPromotionId] = useState<number | null>(null);

    const promotionsData = data?.pages.flatMap((page) => page.results) ?? [];

    function renderPromotionRow(promotion: PromotionListItemType): PromotionsTableRenderingType {
        const statusConfig = getPromotionStatusConfig(promotion.status);

        return {
            key: String(promotion.id),
            title: promotion.title,
            preview: promotion.preview?.url ? (
                <button
                    onClick={() => setActivePromotionId(promotion.id)}
                    style={{background: 'none', border: 'none', cursor: 'pointer', padding: 0}}
                    type="button"
                >
                    <img
                        alt=""
                        src={promotion.preview.url}
                        style={{borderRadius: 4, display: 'block', height: 40, objectFit: 'cover', width: 32}}
                    />
                </button>
            ) : (
                '—'
            ),
            status: (
                <Tag color={statusConfig.tagColor}>
                    <Locale stringKey={statusConfig.langKey} />
                </Tag>
            ),
            startAt: promotion.startAt ? getFormattedDateTime(Date.parse(promotion.startAt)) : '—',
            finishAt: promotion.finishAt ? getFormattedDateTime(Date.parse(promotion.finishAt)) : '—',
            companiesCount: (
                <SelectedCompaniesModal<PromotionSelectedCompanyType>
                    companiesCount={promotion.companiesCount}
                    objectId={promotion.id}
                    renderStatus={(record) => {
                        const catalogStatusConfig = getPromotionCatalogStatusConfig(record.status);

                        return (
                            <Tag color={catalogStatusConfig.tagColor}>
                                <Locale stringKey={catalogStatusConfig.langKey} />
                            </Tag>
                        );
                    }}
                    subtitle={promotion.title}
                    title={getLocalizedString('PROMOTIONS__SELECTED_COMPANIES__TITLE')}
                    useCompanies={usePromotionSelectedCompanies}
                />
            ),
            date: promotion.createdAt ? getFormattedDateTime(Date.parse(promotion.createdAt)) : '—',
            actions: <PromotionActions onEdit={setEditingPromotionId} promotion={promotion} />,
        };
    }

    return (
        <>
            <Table<PromotionsTableRenderingType>
                columns={promotionsTableColumnList}
                dataSource={promotionsData.map(renderPromotionRow)}
                loading={isLoading}
                pagination={false}
                scroll={{x: true}}
                size="middle"
            />

            {hasNextPage && (
                <div style={{marginTop: 16, textAlign: 'center'}}>
                    <Button loading={isFetchingNextPage} onClick={() => fetchNextPage()}>
                        <Locale stringKey="POSTS__STORIES_TABLE__LOAD_MORE" />
                    </Button>
                </div>
            )}

            <PromotionPreview activePromotionId={activePromotionId} onClose={() => setActivePromotionId(null)} />

            {editingPromotionId !== null && (
                <EditPromotionFormModal
                    promotionId={editingPromotionId}
                    setIsOpen={(isOpen) => {
                        if (!isOpen) {
                            setEditingPromotionId(null);
                        }
                    }}
                />
            )}
        </>
    );
}
